// ArticleContent.tsx
import { Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { CustomText } from '../../atoms';
import { ArticleFeedback } from '../../molecules/FaqPage.tsx/ArticleFeedback';
import { useSettings } from '../../../stateManagement/SettingsContext';

interface ArticleContentProps {
  sectionIdForFaq?: string;
  title: string;
  content: string;
  lastUpdated: string;
  createdDate: string;
  textColor?: string;
  bgColor?: string;
  sectionName?: string;
  id: string;
  publishId: string;
  feedbackText: string;
  secondaryAccentColor?: string;
  gradientAccentColor?: string;
  isPreview?: boolean;
  onClickMore?: (title: string) => void;
  onClickLandingPageButton?: () => void;
}

export const ArticleContent: React.FC<ArticleContentProps> = ({
  sectionIdForFaq,
  title,
  content,
  lastUpdated,
  createdDate,
  textColor = '#000',
  bgColor = '#fff',
  sectionName,
  id,
  publishId,
  feedbackText,
  secondaryAccentColor,
  gradientAccentColor,
  isPreview,
  onClickMore,
  onClickLandingPageButton,
}) => {
  const navigate = useNavigate();
  const { language } = useSettings();
  const [day, month, year] = lastUpdated.split('/');
  const formattedDate = new Date(`${year}-${month}-${day}`);
  const breadcrumbHref = `/sections/${language}/section/${sectionName?.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <Box p="10" backgroundColor={bgColor}>
      <Breadcrumb
        separator="/" // Sets the separator between breadcrumb items
        fontSize="xs"
        color={secondaryAccentColor}
      >
        {/* Home Breadcrumb Item */}
        <BreadcrumbItem>
          {isPreview ? (
            <BreadcrumbLink
              onClick={() => onClickLandingPageButton?.()}
              color={secondaryAccentColor}
            >
              Home
            </BreadcrumbLink>
          ) : (
            <a
              href="/"
              onClick={e => {
                const isLeftClick = e.button === 0;
                const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

                if (isLeftClick && !isModified) {
                  e.preventDefault();
                  navigate('/');
                }
              }}
            >
              <BreadcrumbLink color={secondaryAccentColor}>Home</BreadcrumbLink>
            </a>
          )}
        </BreadcrumbItem>

        {/* Category Name Breadcrumb Item */}
        <BreadcrumbItem isCurrentPage>
          {isPreview ? (
            //  SIMPLE SPA NAVIGATION
            <BreadcrumbLink
              onClick={() => onClickMore?.(sectionName || '')}
              color={secondaryAccentColor}
              _hover={{ textDecoration: 'underline' }}
              cursor="pointer"
            >
              {sectionName}
            </BreadcrumbLink>
          ) : (
            //  ANCHOR WITH CMD/MIDDLE CLICK SUPPORT
            <a
              href={breadcrumbHref}
              onClick={e => {
                const isLeftClick = e.button === 0;
                const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

                if (isLeftClick && !isModified) {
                  e.preventDefault();
                  navigate(breadcrumbHref);
                }
              }}
              style={{ color: secondaryAccentColor, textDecoration: 'none' }}
            >
              <BreadcrumbLink as="span" _hover={{ textDecoration: 'underline' }} cursor="pointer">
                {sectionName}
              </BreadcrumbLink>
            </a>
          )}
        </BreadcrumbItem>
      </Breadcrumb>
      <Box my="10">
        <CustomText variant="h1" color={textColor}>
          {title}
        </CustomText>
      </Box>
      {/* <Box mb="10">
        <CustomText
          color="gray.500"
          variant="body"
          fontSize="sm"
        >{`Last updated ${lastUpdated}`}</CustomText>
      </Box> */}
      <Box mb="10" display="flex" gap="2">
        <CustomText color="gray.500" variant="body" fontSize="sm">
          {`Last updated on: ${new Date(formattedDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}`}
        </CustomText>
        <Text fontSize="sm" color="gray.500">
          |
        </Text>
        <CustomText color="gray.500" variant="body" fontSize="sm">
          {`Created on: ${new Date(createdDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}`}
        </CustomText>
      </Box>
      <Box
        className="article-content"
        dangerouslySetInnerHTML={{ __html: content }}
        textColor={textColor}
      />
      <Box height="1px" width="100%" background={gradientAccentColor} mt={10} />
      <ArticleFeedback
        id={id}
        publishId={publishId}
        title={title}
        feedbackText={feedbackText}
        sectionIdForFaq={sectionIdForFaq}
        language={language}
        // category={category}
      />
    </Box>
  );
};
